import React, { ChangeEvent, SetStateAction, Dispatch } from 'react';
import { TrialOrderClItems, TrialOrderClItem, LensProduct, Stockout } from '../types';
import { SelectInput, SelectOption } from '@/components/SelectInput';
import { LensSelect } from './LensDataForm';
import setLensDataInfo from '../api/setLensDataInfo';
import Facility from '../../shared/types/Facility';

type TrialLensDataFormProps = {
  required?: boolean;
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  onChangeFunctions: ((value?: string) => void)[];
  size?: string;
  contactBoxCt?: string;
  lenses: TrialOrderClItems | undefined;
  setLenses: Dispatch<SetStateAction<TrialOrderClItems>>;
  isAstigma: boolean;
  productId?: number;
  products: LensProduct[];
  setArrivalDates: Dispatch<SetStateAction<string[]>>;
  initialArrivalDates: string[];
  setExpectedShipDate: Dispatch<SetStateAction<string>>;
  initialExpectedShipDate: string;
  setShortestArrivalDate: Dispatch<SetStateAction<string>>;
  initialShortestArrivalDate: string;
  facility?: Facility;
}

type LensFormProps = {
  lenses: TrialOrderClItems;
  lens: TrialOrderClItem;
  setLenses: Dispatch<SetStateAction<TrialOrderClItems>>;
  isAstigma: boolean;
  id: string;
  products: LensProduct[];
  setArrivalDates: Dispatch<SetStateAction<string[]>>;
  initialArrivalDates: string[];
  setExpectedShipDate: Dispatch<SetStateAction<string>>;
  initialExpectedShipDate: string;
  setShortestArrivalDate: Dispatch<SetStateAction<string>>;
  initialShortestArrivalDate: string;
  facility?: Facility;
}

const LensForm: React.FC<LensFormProps> = ({
  lenses,
  lens,
  setLenses,
  isAstigma,
  id,
  products,
  setArrivalDates,
  initialArrivalDates,
  setExpectedShipDate,
  initialExpectedShipDate,
  setShortestArrivalDate,
  initialShortestArrivalDate,
  facility,
}) => {
  const otherLenses = Object.entries(lenses).filter(([key,]) => key !== id);

  const fcOptions: string[] = Array.from({ length: (47.00 - 39.00) / 0.50 + 1 }, (_, index) => 39.00 + (0.50 * index))
    .map(n => n.toFixed(2))
    .filter(fc => {
      // 他のレンズで同じtpとdiaが使われている場合は、そのfcを除外
      return !(lens.tp === "-7.00" && fc === "39.00")
      && !(lens.tp === "-7.50" && ["39.00", "39.50"].includes(fc))
      && !(lens.tp === "-8.00" && ["39.00", "39.50", "40.00"].includes(fc))
      && !otherLenses.some(([_, otherLens]) =>
        otherLens.tp === lens.tp && otherLens.dia === lens.dia && otherLens.fc === fc
      );
    });

  const tpOptions: string[] = Array.from({ length: ((-1.00 - (-8.00)) / 0.50) + 1 }, (_, index) => -8.00 + (0.50 * index))
    .map(n => n.toFixed(2))
    .filter(tp => {
      return !(lens.fc === "39.00" && ["-7.00", "-7.50", "-8.00"].includes(tp))
      && !(lens.fc === "39.50" && ["-7.50", "-8.00"].includes(tp))
      && !(lens.fc === "40.00" && tp === "-8.00")
      && !otherLenses.some(([_, otherLens]) =>
        otherLens.fc === lens.fc && otherLens.dia === lens.dia && otherLens.tp === tp
      );
    });

    tpOptions.sort((a, b) => Math.abs(parseFloat(a)) - Math.abs(parseFloat(b)));

    const cylOptions: string[] = Array.from({ length: ((3.00 - 1.00) / 0.50) + 1 }, (_, index) => 1.00 + (0.50 * index))
    .map(n => n.toFixed(2))
    .filter(cyl => {
      return !otherLenses.some(([_, otherLens]) =>
        otherLens.fc === lens.fc && otherLens.tp === lens.tp && otherLens.dia === lens.dia && otherLens.cyl === cyl
      );
    });

  const diaOptions: string[] = Array.from({ length: ((11.00 - 10.20) / 0.40) + 1 }, (_, index) => 10.20 + (0.40 * index))
    .map(n => n.toFixed(1))
    .filter(dia => {
      return !otherLenses.some(([_, otherLens]) =>
        otherLens.fc === lens.fc && otherLens.tp === lens.tp && otherLens.dia === dia
      );
    });

  const handleLensDataChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: 'fc' | 'tp' | 'cyl' | 'dia' | 'qty',
    id: string
  ) => {
    const { value } = event.target;
    const stringId = id.toString();
    setLenses((prevLenses: TrialOrderClItems): TrialOrderClItems => {
      const existingLens = prevLenses[stringId];
      const updatedLens: TrialOrderClItem = {
        ...existingLens,
        [key]: value,
      };
      return {
        ...prevLenses,
        [stringId]: updatedLens,
      };
    });

    if(lens.productId) {
      const selectedProduct = products.find((product: LensProduct) => product.id === lens.productId);
      if(selectedProduct) {
        const { stockouts } = selectedProduct;
        const checkTpRange = (start: string, end: string, tp: string) => {
          var tStart = parseFloat(start);
          var tEnd = parseFloat(end);
          var arrayTp = [];
          for (tEnd; tEnd <= tStart; tEnd+=0.50){arrayTp.push(tEnd)}
          arrayTp = arrayTp.map((num) => num.toFixed(2));
          return arrayTp.includes(tp);
        }
        const stockout = stockouts.find((stockout: Stockout)  => {
          return  stockout.fc === (key === 'fc' ? value : lens.fc) &&
                  checkTpRange(stockout.tpStart, stockout.tpEnd, (key === 'tp' ? value : lens.tp) ?? '') &&
                  stockout.cyl === (key === 'cyl' ? value : lens.cyl) &&
                  stockout.dia === (key === 'dia' ? value : lens.dia)
        })

        const specialOrder = {
          productId: lens.productId,
          fc: key === 'fc' ? value : lens.fc,
          tp: key === 'tp' ? value : lens.tp,
          dia: key === 'dia' ? value : lens.dia,
          cyl: key === 'cyl' ? value : (lens.cyl === "" ? undefined : lens.cyl)
        }
        const updateLensWithSpecialOrders = async () => {
          const { fc, tp, dia } = specialOrder;
          if (fc !== "" && fc !== undefined && tp !== "" && tp !== undefined && dia !== "" && dia !== undefined) {
            try {
              const response = await setLensDataInfo({
                specialOrder: specialOrder,
                lensDataInfo: {
                  facilityId: facility?.id,
                  expectedShipDate: initialExpectedShipDate
                }
              });

            if (response.data.isSpecialOrder) {
              const data = {
                arrivalDates: response.data.arrivalDates,
                expectedShipDate: response.data.expectedShipDate,
                shortestArrivalDate: response.data.shortestArrivalDate,
              };

              setLenses((prevLenses: TrialOrderClItems): TrialOrderClItems => {
                const existingLens = prevLenses[stringId];

                const updatedLens: TrialOrderClItem = {
                  ...existingLens,
                  arrivalDates: data.arrivalDates,
                  expectedShipDate: data.expectedShipDate,
                  shortestArrivalDate: data.shortestArrivalDate
                };
                return {
                  ...prevLenses,
                  [stringId]: updatedLens,
                };
              });

              checkSpecialOrder(data, id);
            } else {

              setLenses((prevLenses: TrialOrderClItems): TrialOrderClItems => {
                const existingLens = prevLenses[stringId];
                const { arrivalDates, expectedShipDate, shortestArrivalDate, ...updatedLensWithoutSpecialOrder } = existingLens;
                const updatedLens: TrialOrderClItem = updatedLensWithoutSpecialOrder;
                return {
                  ...prevLenses,
                  [stringId]: updatedLens,
                };
              });

              checkSpecialOrder(undefined, stringId);
            }
            } catch (error) {
              console.error("Error fetching special Order data:", error);
            }
          }
        }
        updateLensWithSpecialOrders();
        if (stockout) {
          setLenses((prevLenses: TrialOrderClItems): TrialOrderClItems => {
            const existingLens = prevLenses[stringId];
            const updatedLens: TrialOrderClItem = {
              ...existingLens,
              stockoutMessage: stockout.stockoutMessage,
            };
            return {
              ...prevLenses,
              [stringId]: updatedLens,
            };
          });
        } else {
          setLenses((prevLenses: TrialOrderClItems): TrialOrderClItems => {
            const existingLens = prevLenses[stringId];
            const { stockoutMessage, ...updatedLensWithoutStockoutMessage } = existingLens;
            const updatedLens: TrialOrderClItem = updatedLensWithoutStockoutMessage;
            return {
              ...prevLenses,
              [stringId]: updatedLens,
            };
          });
        }
      }
    }
  };

  const handleRemoveLens = (id: string) => {
    setLenses((prevLenses: TrialOrderClItems) => {
      const newLenses = { ...prevLenses };
      delete newLenses[id];
      return newLenses;
    });

    checkSpecialOrder(undefined, id);
  };

  const checkSpecialOrder = async (data: {expectedShipDate: string, arrivalDates: string[], shortestArrivalDate: string} | undefined, idx: string) => {
    let expectedDates = [
      {
        expectedShipDate: data ? data.expectedShipDate : initialExpectedShipDate,
        arrivalDates: data ? data.arrivalDates : initialArrivalDates,
        shortestArrivalDate: data ? data.shortestArrivalDate : initialShortestArrivalDate
      },
    ]

    Object.entries(lenses as TrialOrderClItems).forEach(([id, lens]) => {
      if(id !== idx && lens.expectedShipDate && lens.arrivalDates && lens.shortestArrivalDate){
        expectedDates.push(
          {
            expectedShipDate: lens.expectedShipDate,
            arrivalDates: lens.arrivalDates,
            shortestArrivalDate: lens.shortestArrivalDate
          },
        )
      }
    })

    expectedDates.sort((a, b) => {
      return new Date(b.expectedShipDate).getTime() - new Date(a.expectedShipDate).getTime();
    });

    setExpectedShipDate(expectedDates[0].expectedShipDate);
    setArrivalDates(expectedDates[0].arrivalDates);
    setShortestArrivalDate(expectedDates[0].shortestArrivalDate);
  }

  return (
    <div className="contact-box__add" id={`lens_data_${id.toString()}`}>
      <div className="stockout-message">
        {lens.stockoutMessage && (
          <p className="stockout-alert" style={{color: 'red', fontSize: '1.4rem'}}>{lens.stockoutMessage}</p>
        )}
      </div>
      <ul className="select-list">
        <LensSelect
          label={"FC"}
          options={fcOptions}
          value={lens.fc || ''}
          onChange={(e) => handleLensDataChange(e, 'fc', id)}
        />
        <LensSelect
          label={"TP"}
          options={tpOptions}
          value={lens.tp || ''}
          onChange={(e) => handleLensDataChange(e, 'tp', id)}
        />
        {isAstigma &&
          <LensSelect
            label={"CYL"}
            options={cylOptions}
            value={lens.cyl || ''}
            onChange={(e) => handleLensDataChange(e, 'cyl', id)}
          />
        }
        <LensSelect
          label={"DIA"}
          options={diaOptions}
          value={lens.dia || ''}
          onChange={(e) => handleLensDataChange(e, 'dia', id)}
        />
      </ul>
      <ul className="select-list">
        <li className="select-list__item">
          <span>数量</span>
          <span style={{paddingLeft: '10px'}}>1</span>
        </li>
      </ul>
      <div className="contact-box__add-close" onClick={() => handleRemoveLens(id)}> ✕ 削除 </div>
    </div>
  )
}

const TrialLensDataForm: React.FC<TrialLensDataFormProps> = ({
  options,
  value,
  placeholder,
  onChangeFunctions,
  size = '',
  lenses,
  setLenses,
  isAstigma,
  productId,
  products,
  setArrivalDates,
  initialArrivalDates,
  setExpectedShipDate,
  initialExpectedShipDate,
  setShortestArrivalDate,
  initialShortestArrivalDate,
  facility,
}) => {

  const addInput = () => {
    const newId = Math.max(0, ...Object.keys(lenses || {}).map(Number)) + 1;
    setLenses({ ...(lenses || {}), [newId]: { fc: '', tp: '', cyl: '', dia: '', qty: 1, eyeType: 'trial', productId: productId } });
  };

  return (
    <div className="contact-box__item contact-box__item--start">
      <label className="contact-box__ttl">
        <span>対象レンズ</span>
        <small>必須</small>
      </label>
      <div className="contact-box__ct">
        <SelectInput
          options={options}
          value={value}
          placeholder={placeholder}
          onChangeFunctions={onChangeFunctions}
          size={size}
          contactBoxCt={''}
          />
        <div id="add" className="mbpc-20 mbsp-15">
          { lenses && Object.entries(lenses).map(([id, lens]) => {
            return (
              <LensForm
                key={id}
                id={id}
                lens={lens as TrialOrderClItem}
                setLenses={setLenses}
                isAstigma={isAstigma}
                products={products}
                setArrivalDates={setArrivalDates}
                initialArrivalDates={initialArrivalDates}
                setExpectedShipDate={setExpectedShipDate}
                initialExpectedShipDate={initialExpectedShipDate}
                setShortestArrivalDate={setShortestArrivalDate}
                initialShortestArrivalDate={initialShortestArrivalDate}
                lenses={lenses}
                facility={facility}
              />
            )
          }) }
        </div>
        <div className="c-btn__03" onClick={ () => addInput()}>注文レンズを追加</div>
      </div>
    </div>
  )
}

export default TrialLensDataForm;